import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

export interface ColumnFieldOption {
  value: string
  labelKey: TranslationKeys
  primary_key?: boolean
}

export interface ColumnTableOption {
  table: string
  labelKey: TranslationKeys
  descriptionKey: TranslationKeys
  kind: "entity" | "junction"
  fields: ColumnFieldOption[]
}

export const COLUMN_MAPPING_OPTIONS: ColumnTableOption[] = [
  {
    table: "suppliers",
    labelKey: "MC_MD_MAP_TABLE_SUPPLIERS",
    descriptionKey: "MC_MD_MAP_TABLE_SUPPLIERS_DESC",
    kind: "entity",
    fields: [
      {
        value: "supplier_number",
        labelKey: "MC_MD_MAP_FIELD_SUPPLIERS_SUPPLIER_NUMBER",
        primary_key: true,
      },
      { value: "name", labelKey: "MC_MD_MAP_FIELD_SUPPLIERS_NAME" },
      { value: "email", labelKey: "MC_MD_MAP_FIELD_SUPPLIERS_EMAIL" },
      { value: "country", labelKey: "MC_MD_MAP_FIELD_SUPPLIERS_COUNTRY" },
      {
        value: "duns_number",
        labelKey: "MC_MD_MAP_FIELD_SUPPLIERS_DUNS_NUMBER",
      },
    ],
  },
  {
    table: "supplier_contacts",
    labelKey: "MC_MD_MAP_TABLE_SUPPLIER_CONTACTS",
    descriptionKey: "MC_MD_MAP_TABLE_SUPPLIER_CONTACTS_DESC",
    kind: "entity",
    fields: [
      { value: "name", labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_NAME" },
      {
        value: "first_name",
        labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_FIRST_NAME",
      },
      {
        value: "last_name",
        labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_LAST_NAME",
      },
      { value: "email", labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_EMAIL" },
      { value: "phone", labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_PHONE" },
      { value: "role", labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_ROLE" },
      {
        value: "is_primary",
        labelKey: "MC_MD_MAP_FIELD_SUPPLIER_CONTACTS_IS_PRIMARY",
      },
    ],
  },
  {
    table: "articles",
    labelKey: "MC_MD_MAP_TABLE_ARTICLES",
    descriptionKey: "MC_MD_MAP_TABLE_ARTICLES_DESC",
    kind: "entity",
    fields: [
      {
        value: "article_number",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_ARTICLE_NUMBER",
        primary_key: true,
      },
      { value: "name", labelKey: "MC_MD_MAP_FIELD_ARTICLES_NAME" },
      {
        value: "manufacturer_id",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_MANUFACTURER_ID",
      },
      {
        value: "manufacturer_name",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_MANUFACTURER_NAME",
      },
      {
        value: "country_of_origin",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_COUNTRY_OF_ORIGIN",
      },
      {
        value: "material_group",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_MATERIAL_GROUP",
      },
      {
        value: "product_group",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_PRODUCT_GROUP",
      },
      {
        value: "scip_number",
        labelKey: "MC_MD_MAP_FIELD_ARTICLES_SCIP_NUMBER",
      },
    ],
  },
  {
    table: "products",
    labelKey: "MC_MD_MAP_TABLE_PRODUCTS",
    descriptionKey: "MC_MD_MAP_TABLE_PRODUCTS_DESC",
    kind: "entity",
    fields: [
      {
        value: "product_number",
        labelKey: "MC_MD_MAP_FIELD_PRODUCTS_PRODUCT_NUMBER",
        primary_key: true,
      },
      { value: "name", labelKey: "MC_MD_MAP_FIELD_PRODUCTS_NAME" },
      { value: "revision", labelKey: "MC_MD_MAP_FIELD_PRODUCTS_REVISION" },
      { value: "status", labelKey: "MC_MD_MAP_FIELD_PRODUCTS_STATUS" },
      {
        value: "scip_number",
        labelKey: "MC_MD_MAP_FIELD_PRODUCTS_SCIP_NUMBER",
      },
    ],
  },
  {
    table: "customers",
    labelKey: "MC_MD_MAP_TABLE_CUSTOMERS",
    descriptionKey: "MC_MD_MAP_TABLE_CUSTOMERS_DESC",
    kind: "entity",
    fields: [
      {
        value: "customer_number",
        labelKey: "MC_MD_MAP_FIELD_CUSTOMERS_CUSTOMER_NUMBER",
        primary_key: true,
      },
      { value: "name", labelKey: "MC_MD_MAP_FIELD_CUSTOMERS_NAME" },
      { value: "email", labelKey: "MC_MD_MAP_FIELD_CUSTOMERS_EMAIL" },
    ],
  },
  {
    table: "substances",
    labelKey: "MC_MD_MAP_TABLE_SUBSTANCES",
    descriptionKey: "MC_MD_MAP_TABLE_SUBSTANCES_DESC",
    kind: "entity",
    fields: [
      {
        value: "cas_number",
        labelKey: "MC_MD_MAP_FIELD_SUBSTANCES_CAS_NUMBER",
      },
      { value: "ec_number", labelKey: "MC_MD_MAP_FIELD_SUBSTANCES_EC_NUMBER" },
      { value: "name", labelKey: "MC_MD_MAP_FIELD_SUBSTANCES_NAME" },
    ],
  },
  {
    table: "product_articles",
    labelKey: "MC_MD_MAP_TABLE_PRODUCT_ARTICLES",
    descriptionKey: "MC_MD_MAP_TABLE_PRODUCT_ARTICLES_DESC",
    kind: "junction",
    fields: [
      {
        value: "quantity",
        labelKey: "MC_MD_MAP_FIELD_PRODUCT_ARTICLES_QUANTITY",
      },
      { value: "unit", labelKey: "MC_MD_MAP_FIELD_PRODUCT_ARTICLES_UNIT" },
      {
        value: "position_number",
        labelKey: "MC_MD_MAP_FIELD_PRODUCT_ARTICLES_POSITION_NUMBER",
      },
    ],
  },
  {
    table: "article_bom",
    labelKey: "MC_MD_MAP_TABLE_ARTICLE_BOM",
    descriptionKey: "MC_MD_MAP_TABLE_ARTICLE_BOM_DESC",
    kind: "junction",
    fields: [
      {
        value: "parent_ref",
        labelKey: "MC_MD_MAP_FIELD_ARTICLE_BOM_PARENT_REF",
        primary_key: true,
      },
      { value: "level", labelKey: "MC_MD_MAP_FIELD_ARTICLE_BOM_LEVEL" },
      { value: "quantity", labelKey: "MC_MD_MAP_FIELD_ARTICLE_BOM_QUANTITY" },
      { value: "unit", labelKey: "MC_MD_MAP_FIELD_ARTICLE_BOM_UNIT" },
      {
        value: "position_number",
        labelKey: "MC_MD_MAP_FIELD_ARTICLE_BOM_POSITION_NUMBER",
      },
    ],
  },
  {
    table: "compliance",
    labelKey: "MC_MD_MAP_TABLE_COMPLIANCE",
    descriptionKey: "MC_MD_MAP_TABLE_COMPLIANCE_DESC",
    kind: "junction",
    fields: [
      {
        value: "regulation_name",
        labelKey: "MC_MD_MAP_FIELD_COMPLIANCE_REGULATION_NAME",
      },
      { value: "status", labelKey: "MC_MD_MAP_FIELD_COMPLIANCE_STATUS" },
      {
        value: "exemption_code",
        labelKey: "MC_MD_MAP_FIELD_COMPLIANCE_EXEMPTION_CODE",
      },
    ],
  },
  {
    table: "mappings",
    labelKey: "MC_MD_MAP_TABLE_MAPPINGS",
    descriptionKey: "MC_MD_MAP_TABLE_MAPPINGS_DESC",
    kind: "junction",
    fields: [
      {
        value: "supplier_article_number",
        labelKey: "MC_MD_MAP_FIELD_MAPPINGS_SUPPLIER_ARTICLE_NUMBER",
        primary_key: true,
      },
      {
        value: "customer_article_number",
        labelKey: "MC_MD_MAP_FIELD_MAPPINGS_CUSTOMER_ARTICLE_NUMBER",
        primary_key: true,
      },
    ],
  },
  {
    table: "substance_data",
    labelKey: "MC_MD_MAP_TABLE_SUBSTANCE_DATA",
    descriptionKey: "MC_MD_MAP_TABLE_SUBSTANCE_DATA_DESC",
    kind: "junction",
    fields: [
      {
        value: "concentration_pct",
        labelKey: "MC_MD_MAP_FIELD_SUBSTANCE_DATA_CONCENTRATION_PCT",
      },
    ],
  },
  {
    table: "additional_attributes",
    labelKey: "MC_MD_MAP_TABLE_CUSTOM_FIELD",
    descriptionKey: "MC_MD_MAP_TABLE_CUSTOM_FIELD_DESC",
    kind: "junction",
    fields: [
      {
        value: "on_article",
        labelKey: "MC_MD_MAP_FIELD_CUSTOM_FIELD_ON_ARTICLE",
      },
      {
        value: "on_supplier",
        labelKey: "MC_MD_MAP_FIELD_CUSTOM_FIELD_ON_SUPPLIER",
      },
      {
        value: "on_product",
        labelKey: "MC_MD_MAP_FIELD_CUSTOM_FIELD_ON_PRODUCT",
      },
      {
        value: "on_customer",
        labelKey: "MC_MD_MAP_FIELD_CUSTOM_FIELD_ON_CUSTOMER",
      },
    ],
  },
]
